import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, BadgeCheck, CalendarDays, Check, Clock3, CreditCard, Gift, Image as ImageIcon, Mail, MapPin, Pencil, Phone, Power, PowerOff, Settings2, Store, Trash2, UserCheck, X } from 'lucide-react';
import toast from 'react-hot-toast';
import Sidebar from '../../components/Sidebar';
import Navbar from '../../components/Navbar';
import Select from '../../components/Select';
import ContactVerifyRow from '../../components/ContactVerifyRow';
import API from '../../utils/api';
import { initialFor, mediaUrl } from '../Profiles/profileShared';
import ProviderServiceManager from './ProviderServiceManager';
import { useConfirmDialog } from '../../hooks/useConfirmDialog';
import './ProviderDetail.css';

const fileUrl = path => (!path ? '' : /^https?:/i.test(path) ? path : mediaUrl(path));
const money = value => (value || value === 0 ? `₹${Number(value).toLocaleString('en-IN')}` : '');
const dateText = value => (value ? new Date(value).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '-');

const Pill = ({ ok, yes, no, YesIcon = Check, NoIcon = Clock3 }) => (
    <span className={`pd-pill ${ok ? 'is-ok' : 'is-wait'}`}>{ok ? <YesIcon size={13} /> : <NoIcon size={13} />}{ok ? yes : no}</span>
);

const Field = ({ label, children }) => (children ? <div className="pd-field"><span>{label}</span><strong>{children}</strong></div> : null);

const Photo = ({ src, alt }) => {
    const [failed, setFailed] = useState(false);
    return failed || !src ? <span className="pd-photo-ph"><ImageIcon size={22} /></span> : <img src={src} alt={alt} loading="lazy" onError={() => setFailed(true)} />;
};

const ProviderDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [provider, setProvider] = useState(null);
    const [services, setServices] = useState([]);
    const [state, setState] = useState('loading'); // loading | ready | missing
    const [managingId, setManagingId] = useState(null); // the service open in the "Manage service" window
    const [confirm, confirmDialog] = useConfirmDialog();

    const load = useCallback(async () => {
        try {
            const res = await API.get(`/admin/vendors/${id}`);
            setProvider(res.data.vendor);
            setServices(res.data.services || []);
            setState('ready');
        } catch (error) {
            setState('missing');
        }
    }, [id]);

    useEffect(() => { load(); }, [load]);

    const act = async (request, okText) => {
        try { await request(); toast.success(okText); load(); } catch (error) { toast.error(error.response?.data?.message || 'Action failed'); }
    };

    // deletes the provider account and everything attached to it (services, packages, bookings, photos), same as the Service Providers list
    const deleteProvider = async () => {
        const label = provider?.businessName || provider?.name || 'this provider';
        if (!(await confirm({
            title: `Delete ${label} permanently?`,
            message: 'This cannot be undone. Tip: download a backup from the Backup page first.',
            items: ['The account and login', 'All services, packages and calendars', 'Bookings and photos'],
            confirmLabel: 'Delete provider', danger: true,
        }))) return;
        try {
            await API.delete(`/admin/vendors/${id}`);
            toast.success('Service provider deleted');
            navigate('/service-providers');
        } catch (error) { toast.error(error.response?.data?.message || 'Could not delete the provider'); }
    };

    // admins who may be assigned to verify this provider (the ones with "can verify profiles" in Admin Access)
    const [verifierAdmins, setVerifierAdmins] = useState([]);
    useEffect(() => {
        API.get('/admin/admins').then(res => setVerifierAdmins((res.data.admins || []).filter(a => a.canVerifyProfiles))).catch(() => {});
    }, []);
    const assignVerifier = (adminId) => act(() => API.put(`/admin/users/${id}/assign-verifier`, { adminId: adminId || null }), 'Verifier updated');

    const verifyService = (service) => act(() => API.put(`/admin/services/${service._id}/verify`, { isVerified: !service.isVerified }), service.isVerified ? 'Service unverified' : 'Service verified');

    const title = <span className="pd-nav-title"><Store size={18} /> Service provider</span>;

    if (state !== 'ready') {
        return (
            <div className="pd-layout"><Sidebar />
                <main className="admin-content pd-content"><Navbar title={title} />
                    <div className="pd-page"><button type="button" className="pd-back" onClick={() => navigate('/service-providers')}><ArrowLeft size={16} /> Back to service providers</button>
                        <div className="pd-card pd-empty">{state === 'loading' ? 'Loading…' : 'This service provider could not be found.'}</div>
                    </div></main></div>
        );
    }

    const name = provider.businessName || provider.name || 'Service provider';
    const address = [provider.addressLine1, provider.addressLine2, provider.city, provider.district, provider.state, provider.country, provider.pincode].filter(Boolean).join(', ');
    const gallery = (provider.photos || []).filter(Boolean);

    return (
        <div className="pd-layout">
            <Sidebar />
            <main className="admin-content pd-content">
                <Navbar title={title} />
                <div className="pd-page">
                    <button type="button" className="pd-back" onClick={() => navigate('/service-providers')}><ArrowLeft size={16} /> Back to service providers</button>

                    <section className="pd-card pd-hero">
                        <span className="pd-logo">{provider.logo ? <Photo src={fileUrl(provider.logo)} alt={name} /> : initialFor(name)}</span>
                        <div className="pd-hero__body">
                            <h1>{name}</h1>
                            <p>{[provider.category, provider.city].filter(Boolean).join(' · ') || 'Wedding service provider'}</p>
                            <span className="pd-public-id">Provider ID · {provider.providerId || 'Pending assignment'}</span>
                            <div className="pd-pills">
                                <Pill ok={provider.isApproved} yes="Approved" no="Pending approval" />
                                <Pill ok={provider.isVerified} yes="Verified" no="Verification pending" YesIcon={BadgeCheck} />
                                <Pill ok={provider.isActive !== false} yes="Active" no="Inactive" YesIcon={Power} NoIcon={PowerOff} />
                                <span className={`pd-pill ${provider.isPremium ? 'is-ok' : ''}`}><CreditCard size={13} />{provider.isPremium ? 'Premium' : 'Free plan'}</span>
                                {provider.isPremium && provider.premiumSource === 'gifted' && <span className="pd-pill is-ok"><Gift size={13} />Gifted by admin</span>}
                            </div>
                        </div>
                        <div className="pd-hero__actions">
                            {provider.isApproved ? (
                                <button type="button" onClick={() => act(() => API.put(`/admin/vendors/${provider._id}/reject`), 'Approval removed')}><X size={15} /> Unapprove</button>
                            ) : (
                                <>
                                    <button type="button" className="is-success" onClick={() => act(() => API.put(`/admin/vendors/${provider._id}/approve`), 'Service provider approved')}><Check size={15} /> Approve</button>
                                    <button type="button" className="is-danger" onClick={() => act(() => API.put(`/admin/vendors/${provider._id}/reject`), 'Service provider rejected')}><X size={15} /> Reject</button>
                                </>
                            )}
                            <button type="button" className={provider.isVerified ? 'is-danger' : 'is-success'} onClick={() => act(() => API.put(`/admin/users/${provider._id}/${provider.isVerified ? 'unverify' : 'verify'}`), provider.isVerified ? 'Provider unverified' : 'Provider verified')}>
                                {provider.isVerified ? <><X size={15} /> Unverify</> : <><Check size={15} /> Verify</>}
                            </button>
                            <button type="button" onClick={() => act(() => API.put(`/admin/users/${provider._id}/toggle-active`), provider.isActive !== false ? 'Account deactivated' : 'Account activated')}>
                                {provider.isActive !== false ? <><PowerOff size={15} /> Deactivate</> : <><Power size={15} /> Activate</>}
                            </button>
                            <button type="button" onClick={() => navigate(`/service-providers/${provider._id}/edit`)}><Pencil size={15} /> Edit details</button>
                            <button type="button" className="is-danger" onClick={deleteProvider}><Trash2 size={15} /> Delete provider</button>
                        </div>
                    </section>

                    <div className="pd-grid">
                        <section className="pd-card">
                            <h2>Contact & business</h2>
                            <Field label="Provider ID">{provider.providerId || 'Pending assignment'}</Field>
                            <ContactVerifyRow label="Mobile" type="mobile" icon={Phone} userId={provider._id} value={provider.mobile} verified={provider.isMobileVerified !== false} onChanged={load} />
                            <ContactVerifyRow label="Email" type="email" icon={Mail} userId={provider._id} value={provider.email} verified={Boolean(provider.isEmailVerified)} onChanged={load} />
                            <Field label="Address"><MapPin size={14} /> {address}</Field>
                            <Field label="Category">{provider.category}</Field>
                            <Field label="Joined"><CalendarDays size={14} /> {dateText(provider.createdAt)}</Field>
                            <div className="pd-field">
                                <span>Assigned verifier</span>
                                <div className="pd-verifier"><UserCheck size={14} />
                                    <Select value={provider.assignedVerifier?._id || ''} onChange={assignVerifier} options={verifierAdmins.map(a => ({ value: a._id, label: a.name }))} placeholder="Unassigned" ariaLabel="Assigned verifier" />
                                </div>
                            </div>
                            {provider.description && <div className="pd-about"><span>About the business</span><p>{provider.description}</p></div>}
                        </section>

                        <section className="pd-card">
                            <h2>Business photos <small>{gallery.length}</small></h2>
                            {gallery.length ? (
                                <div className="pd-gallery">{gallery.map((photo, i) => <a key={`${photo}-${i}`} href={fileUrl(photo)} target="_blank" rel="noopener noreferrer"><Photo src={fileUrl(photo)} alt={`${name} ${i + 1}`} /></a>)}</div>
                            ) : <p className="pd-muted">No business photos uploaded.</p>}
                        </section>
                    </div>

                    <section className="pd-card">
                        <h2>Services <small>{services.length}</small></h2>
                        {services.length === 0 ? <p className="pd-muted">This provider has not added any service yet.</p> : (
                            <div className="pd-services">
                                {services.map(service => (
                                    <article className="pd-service" key={service._id}>
                                        <div className="pd-service__photo"><Photo src={fileUrl(service.photos?.[0])} alt={service.businessName} /></div>
                                        <div className="pd-service__body">
                                            <h3>{service.businessName}</h3>
                                            <p>{[service.category, service.city, service.state].filter(Boolean).join(' · ')}</p>
                                            <div className="pd-service__meta">
                                                {(service.priceMin || service.priceMax) ? <span>{money(service.priceMin)}{service.priceMax ? ` – ${money(service.priceMax)}` : ''}</span> : service.price ? <span>{service.price}</span> : null}
                                                {service.capacity && <span>Capacity {service.capacity}</span>}
                                                <span>{(service.photos || []).length} photos</span>
                                                {service.totalReviews > 0 && <span>★ {Number(service.rating || 0).toFixed(1)} ({service.totalReviews})</span>}
                                            </div>
                                            {service.description && <p className="pd-service__desc">{service.description}</p>}
                                            <div className="pd-pills">
                                                <Pill ok={service.isVerified} yes="Verified" no="Not verified" YesIcon={BadgeCheck} />
                                                <Pill ok={service.isActive !== false} yes="Active" no="Inactive" YesIcon={Power} NoIcon={PowerOff} />
                                                <button type="button" className="pd-mini" onClick={() => verifyService(service)}>{service.isVerified ? 'Unverify' : 'Verify service'}</button>
                                                <button type="button" className="pd-mini pd-mini--solid" onClick={() => setManagingId(service._id)}><Settings2 size={13} /> Manage</button>
                                            </div>
                                        </div>
                                    </article>
                                ))}
                            </div>
                        )}
                    </section>
                </div>
            </main>
            {managingId && <ProviderServiceManager serviceId={managingId} onClose={() => setManagingId(null)} onChanged={load} />}
            {confirmDialog}
        </div>
    );
};

export default ProviderDetail;
